import { useMemo, useState } from 'react'
import { Download, Printer, X } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { BreakdownTable } from './breakdown-table'
import { BreakdownCards } from './breakdown-cards'
import { buildBreakdownColumns, drillTargetOf } from './breakdown-columns'
import type {
  ReportDimension,
  ReportMeasure,
  ReportRow,
} from '@/features/reports/api/use-sales-report-query'
import type { ReportFilters } from '@/features/reports/lib/report-filters'
import type { PeriodGrain, PeriodPreset } from '@/features/reports/lib/period'
import type { ReportColumn } from '@/features/reports/lib/types'
import type { CustomRange } from '@/features/reports/components/period-switcher'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { useLocationStore } from '@/shared/store/location-store'
import { PeriodSwitcher } from '@/features/reports/components/period-switcher'
import { ReportFilterBar } from '@/features/reports/components/report-filter-bar'
import { MeasureSwitcher } from '@/features/reports/components/measure-switcher'
import { DimensionSwitcher } from '@/features/reports/components/dimension-switcher'
import { TimeWindowFilter } from '@/features/reports/components/time-window-filter'
import { ArticleFilter } from '@/features/reports/components/article-filter'
import { ScopeLine } from '@/features/reports/components/scope-line'
import { CaveatNotes } from '@/features/reports/components/caveat-notes'
import { useSalesReportQuery } from '@/features/reports/api/use-sales-report-query'
import { defaultFilters } from '@/features/reports/lib/report-filters'
import {
  describeRange,
  describeResolvedRange,
  resolvePeriod,
} from '@/features/reports/lib/period'
import { hourRowsSpanDays } from '@/features/reports/lib/dimension-labels'
import { exportCsv } from '@/features/reports/lib/export-csv'
import { formatNumber } from '@/features/reports/lib/format'

interface SalesBreakdownSearch {
  dimension?: ReportDimension
  measure?: ReportMeasure
  period?: PeriodPreset
  /** Only meaningful with `period=custom`: the picked window a dashboard tile handed over. */
  from?: string
  to?: string
  fromTime?: string
  toTime?: string
  grain?: PeriodGrain
  categoryId?: number
  /** Comma-separated SKUs, so a narrowed report is a link somebody can send. */
  sku?: string
}

/**
 * One report surface, five dimensions (CHG-2026-09-manager-reporting).
 *
 * The alternative was five report pages that all fetch the same thing differently. Here the
 * dimension switcher picks the grouping, the measure switcher picks the aggregate, and both go to
 * the same endpoint — which is why a dashboard tile can hand its own dimension and filters over and
 * land on a table showing exactly what the tile showed.
 */
export function SalesBreakdownPage({
  search,
}: {
  search: SalesBreakdownSearch
}) {
  return (
    <PermissionGate action={ACTIONS.REPORT_SALES_VIEW}>
      <SalesBreakdownContent search={search} />
    </PermissionGate>
  )
}

function SalesBreakdownContent({ search }: { search: SalesBreakdownSearch }) {
  const { t } = useTranslation('reports')
  const restaurantIds = useLocationStore((s) => s.restaurantIds)

  const [dimension, setDimension] = useState<ReportDimension>(
    search.dimension ?? 'time_day',
  )
  const [measure, setMeasure] = useState<ReportMeasure>(
    search.measure ?? 'revenue',
  )
  const [preset, setPreset] = useState<PeriodPreset>(search.period ?? 'today')
  const [customRange, setCustomRange] = useState<CustomRange | null>(
    search.from && search.to
      ? {
          from: search.from,
          to: search.to,
          fromTime: search.fromTime,
          toTime: search.toTime,
          grain: search.grain,
        }
      : null,
  )
  const [timeWindow, setTimeWindow] = useState({
    daypart: null as ReportFilters['daypart'],
    hourFrom: null as number | null,
    hourTo: null as number | null,
  })
  /** Set by a drill-down: the group whose contents are being shown. */
  const [scope, setScope] = useState<{ id: number; label: string } | null>(
    search.categoryId != null ? { id: search.categoryId, label: '' } : null,
  )
  /** The picked articles. Empty means every article, the same way an empty location list does. */
  const [skus, setSkus] = useState<Array<string>>(
    search.sku ? search.sku.split(',').filter(Boolean) : [],
  )

  const now = useMemo(() => new Date(), [])
  const filters: ReportFilters = {
    ...defaultFilters(restaurantIds),
    preset,
    customFrom: customRange?.from,
    customTo: customRange?.to,
    customFromTime: customRange?.fromTime,
    customToTime: customRange?.toTime,
    customGrain: customRange?.grain,
    restaurantIds,
    skus,
    categoryId: scope?.id ?? null,
    ...timeWindow,
  }

  const report = useSalesReportQuery({
    ...filters,
    dimension,
    measure,
    limit: 1000,
  })
  const resolved = resolvePeriod(preset, now, customRange ?? undefined)

  // What the collapsed filter button says on a phone: the same facts the inline controls show.
  const filterSummary = [
    preset === 'custom' && customRange
      ? describeRange(t, customRange)
      : t(`period.presets.${preset}`),
    timeWindow.daypart
      ? t(`dayparts.${timeWindow.daypart}`)
      : timeWindow.hourFrom != null || timeWindow.hourTo != null
        ? `${timeWindow.hourFrom ?? 0}:00–${timeWindow.hourTo ?? 24}:00`
        : t('timeWindow.allDay'),
    scope ? scope.label || t('groups.scoped') : null,
    skus.length > 0 ? t('articleFilter.count', { value: skus.length }) : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const drillTarget = drillTargetOf(dimension)

  const onDrill = drillTarget
    ? (row: ReportRow) => {
        // Drilling into a group narrows the slice and steps the dimension down one level; drilling
        // into a time bucket only steps the dimension, because the period control owns the window.
        if (dimension === 'category_main' || dimension === 'category_sub') {
          const id = Number(row.key)
          if (Number.isFinite(id)) setScope({ id, label: row.label ?? row.key })
        }
        setDimension(drillTarget)
      }
    : null

  const handleExport = () => {
    const rows = report.data?.rows ?? []
    if (rows.length === 0) return
    const columns = buildBreakdownColumns(t, dimension, measure, {
      withDate: hourRowsSpanDays(rows),
    })
    // The shared exporter takes the reports feature's own column shape; the share column is
    // dropped because it is a bar, and a spreadsheet can divide two numbers itself.
    const csvColumns: Array<ReportColumn<ReportRow>> = columns
      .filter((c) => c.kind !== 'share')
      .map((c) => ({
        key: c.key,
        label: c.label,
        type: 'text',
        accessorFn: c.raw,
      }))
    exportCsv(
      rows,
      csvColumns,
      `${t('csvPrefix')}-${dimension}-${resolved.to.slice(0, 10)}.csv`,
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('breakdown.title')}</h1>
          <ScopeLine
            period={
              preset === 'custom' && customRange
                ? describeRange(t, customRange)
                : describeResolvedRange(t, resolved)
            }
            articleCount={skus.length}
          />
        </div>
        {/* Printing and a CSV download are desktop actions: neither leads anywhere useful from a
            phone, and on a 375px header they cost the row the period control needs. */}
        <div
          className="hidden flex-wrap items-center gap-2 sm:flex"
          data-print-hide
        >
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="mr-1 h-4 w-4" />
            {t('print')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={(report.data?.rows.length ?? 0) === 0}
          >
            <Download className="mr-1 h-4 w-4" />
            {t('exportCsv')}
          </Button>
        </div>
      </div>

      {/* One filter row, above everything it scopes — inline on a desktop, one summary button
          opening a sheet on a phone. */}
      <ReportFilterBar summary={filterSummary}>
        <PeriodSwitcher
          value={preset}
          custom={customRange}
          onChange={(next, range) => {
            setPreset(next)
            if (range) setCustomRange(range)
          }}
        />
        <TimeWindowFilter value={timeWindow} onChange={setTimeWindow} />
        <ArticleFilter filters={filters} selected={skus} onChange={setSkus} />
        {scope ? (
          <Badge variant="secondary" className="h-9 gap-1.5 px-3">
            {scope.label || t('groups.scoped')}
            <button
              type="button"
              onClick={() => setScope(null)}
              aria-label={t('groups.clearScope')}
              className="rounded-sm hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </Badge>
        ) : null}
      </ReportFilterBar>

      {/* Switches, not information: on paper they read as buttons nobody can press, and what they
          are set to is already in the first column's header and the leading figure. */}
      <div
        className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
        data-print-hide
      >
        <DimensionSwitcher value={dimension} onChange={setDimension} />
        <div className="flex items-center gap-3">
          <MeasureSwitcher value={measure} onChange={setMeasure} />
          <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
            {t('rowCount', { count: report.data?.totalRows ?? 0 })}
          </span>
        </div>
      </div>

      {report.error ? (
        <Alert variant="destructive">
          <AlertTitle>{t('errorTitle')}</AlertTitle>
          <AlertDescription>{t('errorDescription')}</AlertDescription>
        </Alert>
      ) : null}

      <div
        className={
          report.isFetching && report.data
            ? 'opacity-60 transition-opacity'
            : undefined
        }
      >
        {/* The same data twice, never both at once: a phone gets cards, a desktop the table the
            owner asked for. Print always takes the table — a printed stack of cards wastes paper. */}
        <div className="hidden lg:block print:block">
          <BreakdownTable
            dimension={dimension}
            measure={measure}
            rows={report.data?.rows ?? []}
            total={report.data?.total ?? EMPTY_TOTALS}
            previous={report.data?.previous}
            onDrill={onDrill}
            loading={report.isLoading}
          />
        </div>
        <div className="lg:hidden print:hidden">
          <BreakdownCards
            dimension={dimension}
            measure={measure}
            rows={report.data?.rows ?? []}
            total={report.data?.total ?? EMPTY_TOTALS}
            previous={report.data?.previous}
            onDrill={onDrill}
            loading={report.isLoading}
          />
        </div>
      </div>

      {/* An instruction about clicking rows has nothing to say on a sheet of paper. The truncation
          note below does, and stays. */}
      <p className="text-xs leading-5 text-muted-foreground" data-print-hide>
        {drillTarget ? t('breakdown.drillHint') : t('breakdown.noDrillHint')}
      </p>

      {report.data && report.data.totalRows > report.data.rows.length ? (
        <p className="text-xs text-muted-foreground">
          {t('breakdown.truncated', {
            shown: formatNumber(report.data.rows.length),
            total: formatNumber(report.data.totalRows),
          })}
        </p>
      ) : null}

      <CaveatNotes caveats={report.data?.caveats ?? []} />
    </div>
  )
}

const EMPTY_TOTALS = { revenueCents: 0, units: 0, bons: 0, skus: 0 }
